import { theme } from "antd";
import type { ThemeConfig } from "antd";
import { hexToRgbTriplet } from "./color";
import { designTokens } from "./designTokens";
import type { ResolvedTheme } from "./themeMode";

/** Maps design tokens onto antd for one mode. Pure; the caller memoizes. */
export function buildAntdTheme(mode: ResolvedTheme, opts: { thai: boolean }): ThemeConfig {
  const { brand: b, radius, size, font, levels } = designTokens;
  const m = designTokens.modes[mode];
  const w = levels.working;
  const hard = (px: number) => `${px}px ${px}px 0 ${m.outline}`;
  const control = {
    colorBgContainer: m.surface2,
    colorBorder: m.controlBorder,
    hoverBorderColor: m.controlBorder,
    activeBorderColor: m.controlBorder,
    activeBg: m.surface2,
    hoverBg: m.surface2,
    activeShadow: `0 0 0 4px ${b.butter}`,
    errorActiveShadow: `0 0 0 4px ${b.butter}`,
    lineWidth: w.outline,
  };

  return {
    cssVar: true,
    hashed: false,
    algorithm: mode === "dark" ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: m.text,
      colorLink: m.text,
      colorLinkHover: m.textMuted,
      colorSuccess: m.successText,
      colorWarning: m.warningText,
      colorError: m.red,
      colorInfo: m.infoText,
      colorBgBase: m.bg,
      colorBgLayout: m.bg,
      colorBgContainer: m.surface,
      colorBgElevated: m.surface,
      colorTextBase: m.text,
      colorText: m.text,
      colorTextSecondary: m.textMuted,
      colorTextTertiary: m.textMuted,
      // Text antd puts on solid colors (Badge counts, solid Tags): white on light-mode red passes AA, bg-ink on dark red.
      colorTextLightSolid: mode === "light" ? m.onRed : m.bg,
      colorBorder: m.controlBorder,
      colorBorderSecondary: m.hairline,
      colorSplit: m.hairline,
      fontFamily: font.body,
      fontSize: opts.thai ? size.fontSizeThai : size.fontSize,
      lineHeight: opts.thai ? size.lineHeightThai : size.lineHeight,
      fontSizeHeading1: 30,
      fontSizeHeading2: 24,
      fontSizeHeading3: 19,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      controlHeight: size.controlHeight,
      borderRadius: radius.button,
      borderRadiusLG: radius.panel,
      borderRadiusSM: 8,
      boxShadow: hard(w.panelShadow),
      boxShadowSecondary: hard(w.panelShadow),
      colorBgMask: `rgba(${hexToRgbTriplet(b.onColor)}, 0.55)`,
      controlOutline: "transparent",
      wireframe: false,
    },
    components: {
      Button: {
        colorPrimary: b.butter,
        colorPrimaryHover: b.butter,
        colorPrimaryActive: b.butter,
        primaryColor: b.onColor,
        primaryShadow: hard(w.buttonShadow),
        defaultShadow: "none",
        dangerShadow: "none",
        // Text on a solid red danger button; antd's default white fails AA on light-mode red.
        dangerColor: m.onRed,
        // antd derives a lighter red for hover/press; keep the AA-checked red (press feedback is the translate).
        colorErrorHover: m.red,
        colorErrorActive: m.red,
        defaultBg: m.surface,
        defaultColor: m.text,
        defaultBorderColor: m.controlBorder,
        defaultHoverBg: m.surface2,
        defaultHoverColor: m.text,
        defaultHoverBorderColor: m.controlBorder,
        defaultActiveBg: m.surface2,
        defaultActiveColor: m.text,
        defaultActiveBorderColor: m.controlBorder,
        colorLink: m.text,
        colorLinkHover: m.textMuted,
        lineWidth: w.outline,
        fontWeight: 600,
      },
      Input: control,
      InputNumber: control,
      DatePicker: control,
      Select: {
        colorBgContainer: m.surface2,
        selectorBg: m.surface2,
        colorBorder: m.controlBorder,
        hoverBorderColor: m.controlBorder,
        activeBorderColor: m.controlBorder,
        activeOutlineColor: b.butter,
        optionSelectedBg: b.butter,
        optionSelectedColor: b.onColor,
        optionActiveBg: m.surface2,
        lineWidth: w.outline,
      },
      Checkbox: { colorPrimary: b.butter, colorPrimaryHover: b.butter, colorBorder: m.controlBorder, lineWidth: w.outline },
      Radio: {
        colorPrimary: b.butter,
        colorBorder: m.controlBorder,
        lineWidth: w.outline,
        buttonSolidCheckedBg: b.butter,
        buttonSolidCheckedColor: b.onColor,
        buttonSolidCheckedHoverBg: b.butter,
        buttonSolidCheckedActiveBg: b.butter,
      },
      Switch: { colorPrimary: b.butter, colorPrimaryHover: b.butter, handleBg: m.surface },
      Segmented: {
        itemSelectedBg: b.butter,
        itemSelectedColor: b.onColor,
        trackBg: m.surface2,
        itemColor: m.textMuted,
        itemHoverColor: m.text,
        itemHoverBg: m.surface,
      },
      Tabs: { inkBarColor: b.butter, itemSelectedColor: m.text, itemHoverColor: m.text, itemColor: m.textMuted, lineWidthBold: 3 },
      Steps: { colorPrimary: b.butter, colorTextLightSolid: b.onColor },
      Menu: {
        itemBg: "transparent",
        itemSelectedBg: b.butter,
        itemSelectedColor: b.onColor,
        itemHoverBg: m.surface2,
        itemHoverColor: m.text,
        itemColor: m.textMuted,
        itemBorderRadius: radius.button,
        // Task 9's .sidebar .ant-menu-inline padding (8px) puts sidebar items 16px from the sider edge.
        itemMarginInline: 8,
      },
      Card: {
        colorBorderSecondary: m.outline,
        lineWidth: w.outline,
        borderRadiusLG: radius.panel,
        headerBg: "transparent",
        // Panel title size (spec §4.2); the head keeps antd's 56px min-height.
        headerFontSize: 19,
      },
      Modal: {
        contentBg: m.surface,
        headerBg: m.surface,
        footerBg: m.surface,
        titleFontSize: 19,
      },
      // Status tags: antd reads color{X} (text), color{X}Bg and color{X}Border for success,
      // processing (Info), error and warning. Plan tags use the default chip.
      Tag: {
        defaultBg: m.chip,
        defaultColor: m.chipText,
        colorSuccess: b.onColor,
        colorSuccessBg: b.mint,
        colorSuccessBorder: b.mint,
        colorInfo: b.onColor,
        colorInfoBg: b.sky,
        colorInfoBorder: b.sky,
        colorError: m.onRed,
        colorErrorBg: m.red,
        colorErrorBorder: m.red,
        colorWarning: b.onColor,
        colorWarningBg: b.butter,
        colorWarningBorder: b.butter,
      },
      // Alerts are surface panels with an ink outline; levels.scss adds the colored icon tile.
      Alert: {
        colorInfoBg: m.surface,
        colorSuccessBg: m.surface,
        colorWarningBg: m.surface,
        colorErrorBg: m.surface,
        colorInfoBorder: m.outline,
        colorSuccessBorder: m.outline,
        colorWarningBorder: m.outline,
        colorErrorBorder: m.outline,
        lineWidth: w.outline,
      },
      Breadcrumb: {
        itemColor: m.textMuted,
        linkColor: m.textMuted,
        linkHoverColor: m.text,
        separatorColor: m.textMuted,
        lastItemColor: m.text,
        fontSize: 12,
      },
      Table: {
        headerBg: m.surface2,
        rowHoverBg: m.surface2,
        rowSelectedBg: m.surface2,
        rowSelectedHoverBg: m.surface2,
        headerColor: m.textMuted,
        borderColor: m.hairline,
        headerSplitColor: "transparent",
      },
      Tooltip: { colorBgSpotlight: m.text, colorTextLightSolid: m.bg },
      Progress: { defaultColor: b.mint, remainingColor: m.surface2 },
      Layout: { headerBg: "transparent", siderBg: m.sidebar, bodyBg: m.bg },
      Typography: { colorLink: m.text, colorLinkHover: m.textMuted },
      Dropdown: { colorBgElevated: m.surface },
      Popover: { colorBgElevated: m.surface },
      Notification: { colorBgElevated: m.surface },
    },
  };
}
